import { useEffect, useState } from "react";
import {
  flowChecks as api,
  type FlowCheck,
  type FlowCheckStatus,
} from "../api";

const FILTERS: { value: "" | FlowCheckStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "executed", label: "Approved" },
  { value: "canceled", label: "Rejected" },
  { value: "", label: "All" },
];

// External-status maps onto the shared pill palette: pending → amber,
// executed → green, canceled → red.
const PILL_CLASS: Record<FlowCheckStatus, string> = {
  pending: "status-submitted",
  executed: "status-executed",
  canceled: "status-rejected",
};
const PILL_LABEL: Record<FlowCheckStatus, string> = {
  pending: "Pending",
  executed: "Approved · executed",
  canceled: "Rejected · canceled",
};

export function FlowChecks() {
  const [items, setItems] = useState<FlowCheck[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"" | FlowCheckStatus>("pending");

  function reload() {
    setItems(null);
    setError(null);
    api
      .list(filter || undefined)
      .then((d) => setItems(d.checks))
      .catch((e) => setError(e.message));
  }

  useEffect(reload, [filter]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Integrations</span>
          <h1>Pre-order checks</h1>
        </div>
      </header>

      <p className="muted">
        Flow instances pushed from the Quantfolio Platform that need an external
        check before they proceed. Validate the flow, then{" "}
        <strong>Approve</strong> (sets <code>externalStatus = executed</code>) or{" "}
        <strong>Reject</strong> (<code>externalStatus = canceled</code>) — the
        decision is pushed back to Quantfolio.
      </p>

      <nav className="tabs">
        {FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            className={filter === f.value ? "tab-link active" : "tab-link"}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </nav>

      {error && <div className="banner-error">{error}</div>}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>
            No {filter === "pending" ? "pending" : filter || ""} flow checks
            {filter ? "" : " yet"}.
          </p>
        </div>
      ) : (
        <div className="mcp-list">
          {items.map((check) => (
            <CheckCard key={check.id} check={check} onChanged={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function CheckCard({
  check,
  onChanged,
}: {
  check: FlowCheck;
  onChanged: () => void;
}) {
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pending = check.external_status === "pending";

  async function decide(decision: "approve" | "reject") {
    setBusy(decision);
    setErr(null);
    try {
      await api.decide(check.id, decision, info.trim() || undefined);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="mcp-server">
      <header className="mcp-server-head">
        <div>
          <h2>{check.flow_name ?? "Flow instance"}</h2>
          <p className="mcp-server-instructions">
            {check.client_name ?? "Unknown client"}
            {check.client_email ? ` · ${check.client_email}` : ""}
          </p>
        </div>
        <span className={`pill ${PILL_CLASS[check.external_status]}`}>
          {PILL_LABEL[check.external_status]}
        </span>
      </header>

      <dl className="mcp-meta">
        <Field label="Flow instance" value={check.flow_instance_id} mono />
        {check.qf_status && <Field label="Quantfolio status" value={check.qf_status} />}
        <Field label="Received" value={formatDate(check.created_at)} />
        {check.decided_at && (
          <Field
            label="Decided"
            value={`${formatDate(check.decided_at)}${
              check.decision_actor ? ` by ${check.decision_actor}` : ""
            }`}
          />
        )}
        {check.external_status_info && (
          <Field label="Reason" value={check.external_status_info} />
        )}
      </dl>

      {err && <div className="banner-error">{err}</div>}

      {pending && (
        <div className="card-form">
          <label className="field">
            <span>Decision note (sent to Quantfolio as externalStatusInfo)</span>
            <textarea
              rows={2}
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              placeholder="e.g. KYC verified, funding confirmed — cleared for execution."
            />
          </label>
          <div className="page-head-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy !== null}
              onClick={() => decide("approve")}
            >
              {busy === "approve" ? "Approving…" : "Approve"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy !== null}
              onClick={() => decide("reject")}
            >
              {busy === "reject" ? "Rejecting…" : "Reject"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="info-row">
      <dt>{label}</dt>
      <dd className={mono ? "mono" : undefined}>{value}</dd>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
